/**
 * AgentFlowCanvas — Visual node-based editor for agent workflows.
 *
 * Uses React Flow (@xyflow/react) to render a canvas where users
 * drag-drop agent/tool/condition nodes and connect them to build workflows.
 *
 * Layout:
 * +------------------------------------------------------------------+
 * | Toolbar: [Add Node v] [Auto Layout] [Zoom Fit] [Save]           |
 * +------------------------------------------------------------------+
 * |                                                                    |
 * |  Node palette     |        Canvas (pan/zoom)        | Node Config |
 * |  (left drawer)    |                                  | (right)     |
 * |                   |      [Trigger]                   |             |
 * |  + Trigger        |          |                       | Selected:   |
 * |  + Agent          |      [Agent]                     | Tool Node   |
 * |  + Tool           |       / \                        | Ref: ...    |
 * |  + Condition      |  [Tool] [Tool]                   | Input: ...  |
 * |  + Parallel       |       \ /                        |             |
 * |  + Human Input    |     [Merge]                      |             |
 * |  + Merge          |                                  |             |
 * |                   |                                  |             |
 * +------------------------------------------------------------------+
 */

import React, { useState, useCallback, useRef } from 'react';
import {
  ReactFlow,
  Controls,
  MiniMap,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  Panel,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  Box, Typography, Button, IconButton, Tooltip, Chip, TextField,
  Select, MenuItem, FormControl, InputLabel, Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  FitScreen as FitIcon,
  Save as SaveIcon,
  AutoFixHigh as LayoutIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { nodeTypes, NODE_PALETTE } from './FlowNodeTypes';

// Generate unique node ID
let nodeIdCounter = 0;
const getNodeId = (type) => `${type}_${++nodeIdCounter}`;

/**
 * Props:
 *   colors: theme colors
 *   initialNodes: saved node data (optional)
 *   initialEdges: saved edge data (optional)
 *   onSave: (nodes, edges) => void
 */
const AgentFlowCanvas = ({ colors, initialNodes = [], initialEdges = [], onSave }) => {
  const reactFlowWrapper = useRef(null);
  const [nodes, setNodes, onNodesChange] = useNodesState(
    initialNodes.length > 0 ? initialNodes : [
      {
        id: 'trigger_1',
        type: 'trigger',
        position: { x: 300, y: 50 },
        data: { label: 'User Message', description: 'Workflow entry point', isDark: true },
      },
    ]
  );
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [selectedNode, setSelectedNode] = useState(null);
  const [paletteOpen, setPaletteOpen] = useState(true);

  const isDark = true; // TODO: get from theme

  const onConnect = useCallback((params) => {
    setEdges((eds) => addEdge({
      ...params,
      animated: true,
      style: { stroke: colors.accent || '#22c55e', strokeWidth: 2 },
    }, eds));
  }, [setEdges, colors]);

  const onNodeClick = useCallback((event, node) => {
    setSelectedNode(node);
  }, []);

  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
  }, []);

  const handleAddNode = useCallback((type) => {
    const id = getNodeId(type);
    const palette = NODE_PALETTE.find(n => n.type === type);
    const newNode = {
      id,
      type,
      position: { x: 250 + Math.random() * 200, y: 150 + nodes.length * 100 },
      data: {
        label: palette?.label || type,
        description: palette?.description || '',
        ref: '',
        isDark,
      },
    };
    setNodes((nds) => [...nds, newNode]);
  }, [nodes, setNodes, isDark]);

  const handleDeleteSelected = useCallback(() => {
    if (!selectedNode) return;
    setNodes((nds) => nds.filter((n) => n.id !== selectedNode.id));
    setEdges((eds) => eds.filter((e) => e.source !== selectedNode.id && e.target !== selectedNode.id));
    setSelectedNode(null);
  }, [selectedNode, setNodes, setEdges]);

  const handleUpdateNodeData = useCallback((field, value) => {
    if (!selectedNode) return;
    setNodes((nds) => nds.map((n) =>
      n.id === selectedNode.id ? { ...n, data: { ...n.data, [field]: value } } : n
    ));
    setSelectedNode((prev) => prev ? { ...prev, data: { ...prev.data, [field]: value } } : null);
  }, [selectedNode, setNodes]);

  const handleSave = useCallback(() => {
    if (onSave) onSave(nodes, edges);
  }, [nodes, edges, onSave]);

  const configPanelWidth = selectedNode ? 280 : 0;

  return (
    <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden', position: 'relative' }}>
      {/* Node Palette (left) */}
      {paletteOpen && (
        <Box sx={{
          width: 180, flexShrink: 0, borderRight: `1px solid ${colors.border}`,
          bgcolor: colors.surface, overflow: 'auto', py: 1,
        }}>
          <Typography variant="caption" sx={{
            px: 1.5, color: colors.textSecondary, fontWeight: 600,
            textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.65rem',
          }}>
            Add Node
          </Typography>
          {NODE_PALETTE.map((item) => (
            <Box
              key={item.type}
              onClick={() => handleAddNode(item.type)}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1,
                px: 1.5, py: 0.75, mx: 0.5, mt: 0.5,
                borderRadius: 1, cursor: 'pointer',
                '&:hover': { bgcolor: colors.hover || 'rgba(255,255,255,0.05)' },
              }}
            >
              <Box sx={{
                width: 8, height: 8, borderRadius: '50%',
                bgcolor: {
                  trigger: '#22c55e', agent: '#3b82f6', tool: '#10b981',
                  condition: '#f59e0b', parallel: '#8b5cf6', humanInput: '#ef4444', merge: '#6366f1',
                }[item.type],
              }} />
              <Box>
                <Typography variant="caption" sx={{ color: colors.text, fontWeight: 500, fontSize: '0.75rem', display: 'block' }}>
                  {item.label}
                </Typography>
                <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '0.6rem', lineHeight: 1.1 }}>
                  {item.description}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {/* Canvas */}
      <Box ref={reactFlowWrapper} sx={{ flex: 1, position: 'relative' }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodeClick={onNodeClick}
          onPaneClick={onPaneClick}
          nodeTypes={nodeTypes}
          fitView
          snapToGrid
          snapGrid={[20, 20]}
          defaultEdgeOptions={{
            animated: true,
            style: { stroke: colors.accent || '#22c55e', strokeWidth: 2 },
          }}
          style={{ background: colors.background }}
        >
          <Controls
            style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 4 }}
          />
          <MiniMap
            style={{ background: colors.surface, border: `1px solid ${colors.border}` }}
            nodeColor={(node) => {
              const styles = { trigger: '#22c55e', agent: '#3b82f6', tool: '#10b981', condition: '#f59e0b', parallel: '#8b5cf6', humanInput: '#ef4444', merge: '#6366f1' };
              return styles[node.type] || '#666';
            }}
          />
          <Background color={colors.border} gap={20} />

          {/* Top toolbar */}
          <Panel position="top-left">
            <Box sx={{ display: 'flex', gap: 0.5, bgcolor: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 1, p: 0.5 }}>
              <Tooltip title={paletteOpen ? 'Hide palette' : 'Show palette'}>
                <IconButton size="small" onClick={() => setPaletteOpen(!paletteOpen)}
                  sx={{ color: paletteOpen ? colors.accent : colors.textMuted }}>
                  <AddIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
              {selectedNode && (
                <Tooltip title="Delete selected node">
                  <IconButton size="small" onClick={handleDeleteSelected}
                    sx={{ color: colors.textMuted, '&:hover': { color: colors.error } }}>
                    <DeleteIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              )}
              <Divider orientation="vertical" flexItem sx={{ mx: 0.25, borderColor: colors.border }} />
              <Tooltip title="Save workflow">
                <IconButton size="small" onClick={handleSave} sx={{ color: colors.textMuted, '&:hover': { color: colors.success } }}>
                  <SaveIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Panel>
        </ReactFlow>
      </Box>

      {/* Node Config Panel (right) */}
      {selectedNode && (
        <Box sx={{
          width: configPanelWidth, flexShrink: 0,
          borderLeft: `1px solid ${colors.border}`, bgcolor: colors.surface,
          overflow: 'auto', p: 1.5,
        }}>
          <Typography variant="caption" sx={{
            color: colors.textSecondary, fontWeight: 600,
            textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.65rem', mb: 1, display: 'block',
          }}>
            Node Configuration
          </Typography>

          <Chip label={selectedNode.type} size="small" sx={{
            mb: 1.5, height: 20, fontSize: '0.65rem', fontWeight: 600,
            bgcolor: {
              trigger: 'rgba(34,197,94,0.15)', agent: 'rgba(59,130,246,0.15)', tool: 'rgba(16,185,129,0.15)',
              condition: 'rgba(245,158,11,0.15)', parallel: 'rgba(139,92,246,0.15)',
              humanInput: 'rgba(239,68,68,0.15)', merge: 'rgba(99,102,241,0.15)',
            }[selectedNode.type] || colors.border,
            color: {
              trigger: '#22c55e', agent: '#3b82f6', tool: '#10b981',
              condition: '#f59e0b', parallel: '#8b5cf6', humanInput: '#ef4444', merge: '#6366f1',
            }[selectedNode.type] || colors.text,
          }} />

          <TextField fullWidth size="small" label="Label" value={selectedNode.data.label || ''}
            onChange={(e) => handleUpdateNodeData('label', e.target.value)}
            sx={{ mb: 1.5, '& .MuiInputBase-input': { color: colors.text, fontSize: '0.85rem' }, '& .MuiInputLabel-root': { color: colors.textSecondary }, '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border } }} />

          <TextField fullWidth size="small" label="Description" value={selectedNode.data.description || ''}
            onChange={(e) => handleUpdateNodeData('description', e.target.value)}
            multiline rows={2}
            sx={{ mb: 1.5, '& .MuiInputBase-input': { color: colors.text, fontSize: '0.8rem' }, '& .MuiInputLabel-root': { color: colors.textSecondary }, '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border } }} />

          {/* Type-specific fields */}
          {(selectedNode.type === 'tool' || selectedNode.type === 'agent') && (
            <TextField fullWidth size="small"
              label={selectedNode.type === 'agent' ? 'Agent Name' : 'Script / Entity.Method'}
              value={selectedNode.data.ref || ''}
              onChange={(e) => handleUpdateNodeData('ref', e.target.value)}
              placeholder={selectedNode.type === 'agent' ? 'e.g., inventory_agent' : 'e.g., Receipt.Create'}
              sx={{ mb: 1.5, '& .MuiInputBase-input': { color: colors.text, fontSize: '0.8rem', fontFamily: 'monospace' }, '& .MuiInputLabel-root': { color: colors.textSecondary }, '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border } }} />
          )}

          {selectedNode.type === 'tool' && (
            <>
              <TextField fullWidth size="small" label="Input Variable"
                value={selectedNode.data.inputVar || ''}
                onChange={(e) => handleUpdateNodeData('inputVar', e.target.value)}
                placeholder="e.g., receiptHeader"
                sx={{ mb: 1.5, '& .MuiInputBase-input': { color: colors.text, fontSize: '0.8rem', fontFamily: 'monospace' }, '& .MuiInputLabel-root': { color: colors.textSecondary }, '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border } }} />
              <TextField fullWidth size="small" label="Output Variable"
                value={selectedNode.data.outputVar || ''}
                onChange={(e) => handleUpdateNodeData('outputVar', e.target.value)}
                placeholder="e.g., receiptResult"
                sx={{ mb: 1.5, '& .MuiInputBase-input': { color: colors.text, fontSize: '0.8rem', fontFamily: 'monospace' }, '& .MuiInputLabel-root': { color: colors.textSecondary }, '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border } }} />
            </>
          )}

          {selectedNode.type === 'agent' && (
            <TextField fullWidth size="small" label="Message" multiline rows={3}
              value={selectedNode.data.message || ''}
              onChange={(e) => handleUpdateNodeData('message', e.target.value)}
              placeholder="What to ask the agent..."
              sx={{ mb: 1.5, '& .MuiInputBase-input': { color: colors.text, fontSize: '0.8rem' }, '& .MuiInputLabel-root': { color: colors.textSecondary }, '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border } }} />
          )}

          {selectedNode.type === 'condition' && (
            <TextField fullWidth size="small" label="Condition Expression"
              value={selectedNode.data.condition || ''}
              onChange={(e) => handleUpdateNodeData('condition', e.target.value)}
              placeholder='e.g., context.inventory.available == true'
              sx={{ mb: 1.5, '& .MuiInputBase-input': { color: colors.text, fontSize: '0.8rem', fontFamily: 'monospace' }, '& .MuiInputLabel-root': { color: colors.textSecondary }, '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border } }} />
          )}

          {selectedNode.type === 'humanInput' && (
            <>
              <TextField fullWidth size="small" label="Question"
                value={selectedNode.data.question || ''}
                onChange={(e) => handleUpdateNodeData('question', e.target.value)}
                placeholder="What needs approval?"
                multiline rows={2}
                sx={{ mb: 1.5, '& .MuiInputBase-input': { color: colors.text, fontSize: '0.8rem' }, '& .MuiInputLabel-root': { color: colors.textSecondary }, '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border } }} />
              <TextField fullWidth size="small" label="Approver Role"
                value={selectedNode.data.approverRole || ''}
                onChange={(e) => handleUpdateNodeData('approverRole', e.target.value)}
                placeholder="e.g., supervisor"
                sx={{ mb: 1.5, '& .MuiInputBase-input': { color: colors.text, fontSize: '0.8rem' }, '& .MuiInputLabel-root': { color: colors.textSecondary }, '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border } }} />
            </>
          )}

          {selectedNode.type === 'trigger' && (
            <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
              <InputLabel sx={{ color: colors.textSecondary }}>Trigger Type</InputLabel>
              <Select value={selectedNode.data.triggerType || 'message'} label="Trigger Type"
                onChange={(e) => handleUpdateNodeData('triggerType', e.target.value)}
                sx={{ color: colors.text, '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border } }}>
                <MenuItem value="message">User Message</MenuItem>
                <MenuItem value="event">Event</MenuItem>
                <MenuItem value="schedule">Schedule</MenuItem>
                <MenuItem value="webhook">Webhook</MenuItem>
              </Select>
            </FormControl>
          )}

          <Divider sx={{ my: 1, borderColor: colors.border }} />
          <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '0.6rem' }}>
            ID: {selectedNode.id}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default AgentFlowCanvas;
